import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Customer, CustomerProfile } from '../../../models/customer.model';
import { ValidationService } from '../../../services/validation.service';
import { faUser, faBuilding, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';

export interface CustomerInfoData {
  customer: Partial<Customer>;
  customerProfile: Partial<CustomerProfile>;
}

export interface CustomerInfoChangeEvent {
  data: CustomerInfoData;
  isValid: boolean;
  errors: { [key: string]: string };
}

@Component({
  selector: 'app-customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerInfoComponent),
      multi: true
    }
  ]
})
export class CustomerInfoComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() editMode: boolean = false;
  @Input() customerData: CustomerInfoData | null = null;
  @Input() showValidationErrors: boolean = false;
  @Input() disabled: boolean = false;

  @Output() customerChange = new EventEmitter<CustomerInfoChangeEvent>();
  @Output() validationChange = new EventEmitter<boolean>();

  // Icons
  faUser = faUser;
  faBuilding = faBuilding;
  faPhone = faPhone;
  faEnvelope = faEnvelope;

  customerForm: FormGroup;
  private destroy$ = new Subject<void>();
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private fb: FormBuilder,
    private validationService: ValidationService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): void {
    this.customerForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      contactNo: ['', [
        Validators.required,
        Validators.pattern(/^[0-9+\-\s()]+$/)
      ]],
      email: ['', [
        Validators.required,
        Validators.email
      ]],
      customer_profile_name: ['DEFAULT', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]]
    });
  }

  private initializeForm(): void {
    if (this.customerData) {
      this.customerForm.patchValue({
        name: this.customerData.customer && this.customerData.customer.name || '',
        contactNo: this.customerData.customer && this.customerData.customer.contactNo || '',
        email: this.customerData.customer && this.customerData.customer.email || '',
        customer_profile_name: this.customerData.customerProfile && this.customerData.customerProfile.name || 'DEFAULT'
      });
    }

    if (this.disabled) {
      this.customerForm.disable();
    } else {
      this.customerForm.enable();
    }
  }

  private setupFormSubscription(): void {
    this.customerForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const data: CustomerInfoData = {
          customer: {
            name: value.name,
            contactNo: value.contactNo,
            email: value.email
          },
          customerProfile: {
            name: value.customer_profile_name
          }
        };

        const isValid = this.customerForm.valid;
        const errors = this.getFormErrors();

        this.customerChange.emit({ data, isValid, errors });
        this.validationChange.emit(isValid);
        this.onChange(data);
      });

    this.customerForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validationChange.emit(this.customerForm.valid);
      });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value && typeof value === 'object') {
      // Handle nested structure from customer-form component
      if (value.customer || value.customerProfile) {
        this.customerForm.patchValue({
          name: value.customer && value.customer.name || '',
          contactNo: value.customer && value.customer.contactNo || '',
          email: value.customer && value.customer.email || '',
          customer_profile_name: value.customerProfile && value.customerProfile.name || 'DEFAULT'
        }, { emitEvent: false });
      } else {
        // Handle flat structure
        this.customerForm.patchValue(value, { emitEvent: false });
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.customerForm.disable();
    } else {
      this.customerForm.enable();
    }
  }

  // Validation methods
  shouldShowError(fieldName: string): boolean {
    if (!this.showValidationErrors) return false;
    
    const field = this.customerForm.get(fieldName);
    if (!field) return false;

    return field.invalid && (field.dirty || field.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.customerForm.get(fieldName);
    if (!field || !field.errors) return '';

    const errors = field.errors;
    
    if (errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    
    if (errors['email']) {
      return 'Please enter a valid email address';
    }
    
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }
    
    if (errors['pattern']) {
      if (fieldName === 'contactNo') {
        return 'Please enter a valid contact number';
      }
    }

    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Customer Name',
      contactNo: 'Contact Number',
      email: 'Email',
      customer_profile_name: 'Customer Profile'
    };
    return labels[fieldName] || fieldName;
  }

  private getFormErrors(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};
    
    Object.keys(this.customerForm.controls).forEach(key => {
      const control = this.customerForm.get(key);
      if (control && control.errors && (control.dirty || control.touched)) {
        errors[key] = this.getErrorMessage(key);
      }
    });

    return errors;
  }

  // Public methods for parent components
  markAllAsTouched(): void {
    this.customerForm.markAllAsTouched();
  }

  resetForm(): void {
    this.customerForm.reset();
    this.customerForm.markAsPristine();
    this.customerForm.markAsUntouched();
  }

  isValid(): boolean {
    return this.customerForm.valid;
  }

  getValue(): CustomerInfoData {
    const formValue = this.customerForm.value;
    return {
      customer: {
        name: formValue.name,
        contactNo: formValue.contactNo,
        email: formValue.email
      },
      customerProfile: {
        name: formValue.customer_profile_name
      }
    };
  }

  onBlur(): void {
    this.onTouched();
  }
}